import { format, parseISO, isValid } from 'date-fns'
import type { FkspRequest } from '../types'
import { fkspStatusLabelCs } from './fkspStatusLabels'


export type FkspEkonomExportRow = {
  created_at: string
  requester_name: string
  personal_number: string
  type_label: string
  status_label: string
  amount: string
  doplaceno: string
  datum_zaplaceni: string
  note: string
  processed: string
}

const FKSP_EXPORT_COLUMNS: { key: keyof FkspEkonomExportRow; label: string }[] = [
  { key: 'created_at', label: 'Datum podání' },
  { key: 'requester_name', label: 'Žadatel' },
  { key: 'personal_number', label: 'Os. č.' },
  { key: 'type_label', label: 'Typ' },
  { key: 'status_label', label: 'Stav' },
  { key: 'amount', label: 'Částka' },
  { key: 'doplaceno', label: 'Doplaceno' },
  { key: 'datum_zaplaceni', label: 'Datum zaplacení' },
  { key: 'note', label: 'Poznámka' },
  { key: 'processed', label: 'Zpracováno' },
]

function formatFinancistDatum(value: string | null | undefined): string {
  if (!value) return '—'
  const raw = value.slice(0, 10)
  const d = parseISO(raw)
  return isValid(d) ? format(d, 'dd.MM.yyyy') : value
}

function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return `${value} Kč`
}

export function buildFkspEkonomExportRows(requests: FkspRequest[]): FkspEkonomExportRow[] {
  return requests.map((req) => ({
    created_at: format(new Date(req.created_at), 'dd.MM.yyyy'),
    requester_name: req.requester_name || '—',
    personal_number: req.personal_number || '—',
    type_label: req.type === 'benefity' ? 'Benefity' : 'Připojištění',
    status_label: fkspStatusLabelCs(req.status),
    amount: formatMoney(req.type === 'benefity' ? req.amount : req.fksp_contribution),
    doplaceno: formatMoney(req.financist_doplaceno ?? null),
    datum_zaplaceni: formatFinancistDatum(req.financist_datum),
    note: (req.financist_note || '').trim() || '—',
    processed:
      req.status === 'director_approved'
        ? req.financist_processed_at
          ? 'Ano'
          : 'Ne'
        : '—',
  }))
}

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null
  const m = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"/i.exec(header)
  const raw = m?.[1] || m?.[2]
  if (!raw) return null
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

export async function downloadFkspEkonomListExport(
  format: 'xlsx' | 'pdf',
  rows: FkspEkonomExportRow[],
): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }

  const apiBase =
    (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL ||
    `${window.location.origin}/api`

  const response = await fetch(`${apiBase}/fksp-export-list?role=fksp_financist`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({ format, rows, columns: FKSP_EXPORT_COLUMNS }),
  })

  if (!response.ok) {
    let errorMsg = 'Export se nezdařil'
    try {
      const err = (await response.json()) as { error?: string; details?: string }
      errorMsg = err.error || errorMsg
      if (err.details?.trim()) errorMsg = `${errorMsg}: ${err.details.trim().slice(0, 500)}`
    } catch {
      // ignore
    }
    throw new Error(errorMsg)
  }

  const blob = await response.blob()
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.style.display = 'none'
  a.href = url
  const datePart = new Date().toISOString().slice(0, 10)
  const fromHeader = filenameFromContentDisposition(response.headers.get('Content-Disposition'))
  a.download =
    fromHeader || `FKSP_prehled_${datePart}.${format === 'pdf' ? 'pdf' : 'xlsx'}`
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(url)
  document.body.removeChild(a)
}
